<header>
  <div class="mx-auto max-w-2xl flex items-center justify-between gap-4 px-4 py-4">
    <img
        src="/images/logo-crb.svg"
        alt="Logo Croix Rouge Belgique"
        class="w-1/3 max-w-[300px] object-contain"
      />

    @if (currentCompany()?.logo) {
      <img
        [src]="currentCompany()!.logo!"
        [alt]="'Logo ' + currentCompany()!.name"
        class="max-h-24 w-auto max-w-full object-contain"
      />
    }
  </div>
</header>

<div class="mx-auto max-w-2xl px-4 pt-0 pb-10">
  @if (submittedSuccess()) {
    <div
      class="rounded-lg border border-emerald-200 bg-emerald-50 px-6 py-8 text-emerald-900 shadow-sm"
      role="status"
    >
      <p class="text-center text-lg font-semibold">Merci, votre rendez-vous a bien été enregistré.</p>
      @if (confirmationEmailDisplay(); as em) {
        @if (em.length > 0) {
          <p class="mt-3 text-center text-sm text-emerald-800">
            Un e-mail de confirmation vous a été envoyé à <span class="font-semibold">{{ em }}</span
            >.
          </p>
        }
      }

      <div
        class="mt-6 rounded-md border border-emerald-200/80 bg-white/70 px-4 py-4 text-left text-sm shadow-sm">
        <h3 class="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-emerald-800">
          Récapitulatif
        </h3>
        <dl class="space-y-3 text-slate-800">
          @if (currentCompany(); as co) {
            <div class="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
              <dt class="shrink-0 font-medium text-slate-600 sm:min-w-[8.5rem]">Entreprise</dt>
              <dd class="min-w-0">{{ co.name }}</dd>
            </div>
          }
          @if (selectedRdvTypeLabel(); as typeRecap) {
            @if (typeRecap.length > 0) {
              <div class="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt class="shrink-0 font-medium text-slate-600 sm:min-w-[8.5rem]">Type de don</dt>
                <dd class="min-w-0 font-medium text-slate-900">{{ typeRecap }}</dd>
              </div>
            }
          }
          @if (siteDetails(); as sd) {
            <div class="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
              <dt class="shrink-0 font-medium text-slate-600 sm:min-w-[8.5rem]">Lieu</dt>
              <dd class="min-w-0 font-medium text-slate-900">{{ sd.name }}</dd>
            </div>
            <div class="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
              <dt class="shrink-0 font-medium text-slate-600 sm:min-w-[8.5rem]">Adresse</dt>
              <dd class="min-w-0">
                {{ sd.address }}<br />
                <span class="whitespace-nowrap">{{ sd.zip }}</span>
                {{ sd.city }}
              </dd>
            </div>
            @if (sd.phone) {
              <div class="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt class="shrink-0 font-medium text-slate-600 sm:min-w-[8.5rem]">Téléphone</dt>
                <dd class="min-w-0">
                  <a class="text-red-700 underline decoration-red-700/30" [href]="'tel:' + sd.phone">{{
                    sd.phone
                  }}</a>
                </dd>
              </div>
            }
          } @else if (currentSite(); as cs) {
            <div class="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
              <dt class="shrink-0 font-medium text-slate-600 sm:min-w-[8.5rem]">Lieu</dt>
              <dd class="min-w-0">
                {{ cs.name }}
                @if (cs.region) {
                  <span class="text-slate-600"> — {{ cs.region }}</span>
                }
              </dd>
            </div>
          }
          @if (selectedDate(); as dateIso) {
            <div class="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
              <dt class="shrink-0 font-medium text-slate-600 sm:min-w-[8.5rem]">Date</dt>
              <dd class="min-w-0 capitalize">{{ formatDateFr(dateIso) }}</dd>
            </div>
          }
          @if (selectedSlotLabel()) {
            <div class="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
              <dt class="shrink-0 font-medium text-slate-600 sm:min-w-[8.5rem]">Heure</dt>
              <dd class="min-w-0 font-medium text-slate-900">{{ selectedSlotLabel() }}</dd>
            </div>
          }
          <div class="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
            <dt class="shrink-0 font-medium text-slate-600 sm:min-w-[8.5rem]">Parrainage</dt>
            <dd class="min-w-0">{{ parrainageRecapLabel() }}</dd>
          </div>
        </dl>
      </div>

      <p class="mt-6 text-center text-sm text-emerald-800">
        À bientôt au centre de prélèvement
        @if (siteDetails()?.name ?? currentSite()?.name; as siteName) {
          <span> de {{ siteName }}</span>
        }.
      </p>
    </div>
  } @else {
    @if (currentCompany()?.banner) {
      <img
        [src]="currentCompany()!.banner!"
        [alt]="'Bannière ' + currentCompany()!.name"
        class="w-auto max-w-full object-contain"
      />
    }
    @if (currentCompany()?.introduction_text) {
      <div class="text-left text-sm text-slate-600 my-4 company-introduction [&_a]:text-red-600" [innerHTML]="currentCompany()!.introduction_text!"></div>
    }

    @if (loadingCompany()) {
      <p class="text-center text-slate-600">Chargement de l’entreprise…</p>
    } @else if (companyLoadError()) {
      <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
        {{ companyLoadError() }}
      </div>
    } @else if (!companyActionActive()) {
      <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
        L’action pour votre entreprise/Association est terminée.<br /><br />
        Si vous souhaitez poursuivre votre geste solidaire, nous vous invitons à prendre rendez - vous sur notre site <a class="text-red-600 underline decoration-red-600/30" href="https://www.DonneurDeSang.be" target="_blank">www.DonneurDeSang.be</a>
      </div>
    } @else if (currentCompany()) {
      <nav
        class="mb-8 rounded-xl border border-slate-200 bg-slate-50/90 p-4 shadow-sm"
        aria-label="Étapes du rendez-vous"
      >
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
          <span class="font-medium text-slate-700">Étape {{ currentStep() }} sur {{ wizardStepCount }}</span>
          <span class="text-slate-500">Vous pouvez revenir en arrière en cliquant sur une étape.</span>
        </div>
        <div class="mb-4 h-2 overflow-hidden rounded-full bg-slate-200">
          <div
            class="h-full rounded-full bg-red-600 transition-[width] duration-300 ease-out"
            [style.width.%]="wizardProgressPercent()"
            role="progressbar"
            [attr.aria-valuenow]="currentStep()"
            aria-valuemin="1"
            [attr.aria-valuemax]="wizardStepCount"
            aria-label="Progression dans la prise de rendez-vous"
          ></div>
        </div>
        <ol class="flex flex-col gap-2 space-y-2">
          <li>
            <button
              type="button"
              class="w-full rounded-lg border px-3 py-2.5 text-left text-sm transition hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-600/25 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-slate-200"
              [class.border-red-600]="isWizardStepActive(1)"
              [class.bg-red-50]="isWizardStepActive(1)"
              [class.shadow-sm]="isWizardStepActive(1)"
              [class.border-slate-200]="!isWizardStepActive(1)"
              [class.bg-white]="!isWizardStepActive(1)"
              [disabled]="!isWizardStepReachable(1)"
              [attr.aria-current]="isWizardStepActive(1) ? 'step' : null"
              (click)="goToWizardStep(1)"
            >
              <span class="font-semibold text-slate-900">1. Lieu de prélèvement *</span>
              @if (siteDetails()?.name ?? currentSite()?.name; as siteName) {
                <span class="mt-1 block text-sm font-normal text-slate-600">{{ siteName }}</span>
              }
            </button>
          </li>
          <li>
            <button
              type="button"
              class="w-full rounded-lg border px-3 py-2.5 text-left text-sm transition hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-600/25 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-slate-200"
              [class.border-red-600]="isWizardStepActive(2)"
              [class.bg-red-50]="isWizardStepActive(2)"
              [class.shadow-sm]="isWizardStepActive(2)"
              [class.border-slate-200]="!isWizardStepActive(2)"
              [class.bg-white]="!isWizardStepActive(2)"
              [disabled]="!isWizardStepReachable(2)"
              [attr.aria-current]="isWizardStepActive(2) ? 'step' : null"
              (click)="goToWizardStep(2)"
            >
              <span class="font-semibold text-slate-900">2. Type de rendez-vous *</span>
              @if (selectedRdvTypeLabel(); as typeLb) {
                @if (typeLb.length > 0) {
                  <span class="mt-1 block text-sm font-normal text-slate-600">{{ typeLb }}</span>
                }
              }
            </button>
          </li>
          <li>
            <button
              type="button"
              class="w-full rounded-lg border px-3 py-2.5 text-left text-sm transition hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-600/25 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-slate-200"
              [class.border-red-600]="isWizardStepActive(3)"
              [class.bg-red-50]="isWizardStepActive(3)"
              [class.shadow-sm]="isWizardStepActive(3)"
              [class.border-slate-200]="!isWizardStepActive(3)"
              [class.bg-white]="!isWizardStepActive(3)"
              [disabled]="!isWizardStepReachable(3)"
              [attr.aria-current]="isWizardStepActive(3) ? 'step' : null"
              (click)="goToWizardStep(3)"
            >
              <span class="font-semibold text-slate-900">3. Date *</span>
              @if (selectedDate(); as dIso) {
                <span class="mt-1 block text-sm font-normal capitalize text-slate-600">{{
                  formatDateFr(dIso)
                }}</span>
              }
            </button>
          </li>
          <li>
            <button
              type="button"
              class="w-full rounded-lg border px-3 py-2.5 text-left text-sm transition hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-600/25 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-slate-200"
              [class.border-red-600]="isWizardStepActive(4)"
              [class.bg-red-50]="isWizardStepActive(4)"
              [class.shadow-sm]="isWizardStepActive(4)"
              [class.border-slate-200]="!isWizardStepActive(4)"
              [class.bg-white]="!isWizardStepActive(4)"
              [disabled]="!isWizardStepReachable(4)"
              [attr.aria-current]="isWizardStepActive(4) ? 'step' : null"
              (click)="goToWizardStep(4)"
            >
              <span class="font-semibold text-slate-900">4. Horaire *</span>
              @if (selectedSlotLabel(); as slotLb) {
                @if (slotLb.length > 0) {
                  <span class="mt-1 block text-sm font-normal text-slate-600">{{ slotLb }}</span>
                }
              }
            </button>
          </li>
          <li>
            <button
              type="button"
              class="w-full rounded-lg border px-3 py-2.5 text-left text-sm transition hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-600/25 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-slate-200"
              [class.border-red-600]="isWizardStepActive(5)"
              [class.bg-red-50]="isWizardStepActive(5)"
              [class.shadow-sm]="isWizardStepActive(5)"
              [class.border-slate-200]="!isWizardStepActive(5)"
              [class.bg-white]="!isWizardStepActive(5)"
              [disabled]="!isWizardStepReachable(5)"
              [attr.aria-current]="isWizardStepActive(5) ? 'step' : null"
              (click)="goToWizardStep(5)"
            >
              <span class="font-semibold text-slate-900">5. Coordonnées *</span>
            </button>
          </li>
        </ol>
      </nav>

      <div class="min-h-[8rem]">
        @switch (currentStep()) {
          @case (1) {
            <div class="space-y-8">
              <section class="space-y-3">
                <h2 class="sr-only">Lieu de prélèvement</h2>
                @if (sitesByRegion().length === 0 && sitesError()) {
                  <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {{ sitesError() }}
                  </div>
                }
                @if (sitesByRegion().length > 0) {
                  <p class="text-sm text-slate-600 text-center font-bold">
                    Choisissez le centre de prélèvement dans la province de votre choix
                  </p>

                  <div class="space-y-2 border-slate-200 pt-4">
                    @for (grp of filteredRegions(); track grp.region) {
                      <div class="rounded-md border border-slate-200">
                        <button
                          type="button"
                          class="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
                          (click)="toggleRegion(grp.region)"
                        >
                          {{ grp.region }}
                          <span class="text-slate-500">{{ isRegionExpanded(grp.region) ? '−' : '+' }}</span>
                        </button>
                        @if (isRegionExpanded(grp.region)) {
                          <div class="space-y-1 border-t border-slate-100 px-3 py-2">
                            @for (site of grp.sites; track site.id) {
                              <label
                                class="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50"
                                [class.bg-red-50]="isSiteSelected(site.id)"
                                (click)="selectSite(site)"
                              >
                                <input
                                  type="radio"
                                  name="site_id"
                                  class="pointer-events-none text-red-600 focus:ring-red-600"
                                  [value]="asText(site.id)"
                                  [checked]="isSiteSelected(site.id)"
                                />
                                <span class="text-sm text-slate-800">{{ site.name }}</span>
                              </label>
                            }
                          </div>
                        }
                      </div>
                    }
                    @if (filteredRegions().length === 0) {
                      <p class="text-sm text-slate-500">Aucun lieu ne correspond au filtre.</p>
                    }
                  </div>
                }
              </section>
            </div>
          }
          @case (2) {
            @if (selectedSiteId() !== null) {
              <section class="space-y-4">
                <h2 class="sr-only">Type de rendez-vous</h2>
                <p class="text-sm text-slate-600 text-center font-bold">Indiquez le type de don pour lequel vous prenez rendez-vous.</p>
                <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap justify-center">
                  <div>
                    <button
                      type="button"
                      class="cursor-pointer rounded-md border px-4 py-3 text-center text-sm font-medium transition sm:min-w-[9rem] bg-sang text-white"
                      [class.outline-sang]="isRdvTypeSelected('sang')"
                      [class.outline-2]="isRdvTypeSelected('sang')"
                      [class.offset-2]="isRdvTypeSelected('sang')"
                      [class.solid]="isRdvTypeSelected('sang')"
                      (click)="selectRdvType('sang')"
                    >
                      Sang
                    </button>
                    <p class="text-center text-sm text-slate-600 mt-1 italic">~ 45 min</p>
                  </div>
                  <div>
                    <button
                      type="button"
                      class="cursor-pointer rounded-md border px-4 py-3 text-center text-sm font-medium transition sm:min-w-[9rem] bg-plasma text-white"
                      [class.outline-plasma]="isRdvTypeSelected('plasma')"
                      [class.outline-2]="isRdvTypeSelected('plasma')"
                      [class.offset-2]="isRdvTypeSelected('plasma')"
                      [class.solid]="isRdvTypeSelected('plasma')"
                      (click)="selectRdvType('plasma')"
                    >
                      Plasma
                    </button>
                    <p class="text-center text-sm text-slate-600 mt-1 italic">~ 1h15</p>
                  </div>
                  <div>
                    <button
                      type="button"
                      class="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-md border px-4 py-3 text-sm font-medium transition sm:min-w-[9rem] bg-plaquettes text-white"
                      [class.outline-plaquettes]="isRdvTypeSelected('plaquettes')"
                      [class.outline-2]="isRdvTypeSelected('plaquettes')"
                      [class.offset-2]="isRdvTypeSelected('plaquettes')"
                      [class.solid]="isRdvTypeSelected('plaquettes')"
                      (click)="selectRdvType('plaquettes')"
                    >
                      <svg
                        class="h-4 w-4 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <span>Plaquettes</span>
                    </button>
                    <p class="text-center text-sm text-slate-600 mt-1 italic">~ 2h</p>
                  </div>
                </div>
                @if (isRdvTypeSelected('plaquettes')) {
                  <div
                    class="rounded-lg border border-amber-300 bg-amber-50 px-4 py-4 text-sm text-amber-950 shadow-sm"
                    role="status"
                  >
                    <p class="leading-relaxed">
                      Il est impossible de prendre rendez-vous en ligne pour donner des plaquettes.<br />
                      Veuillez contacter le centre de prélèvement pour vous inscrire.
                    </p>
                    @if (siteDetails()?.phone; as tel) {
                      @if (tel.trim().length > 0) {
                        <p class="text-center mt-4">
                          <a
                            class="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
                            [href]="siteTelHref(tel)"
                          >
                            Appeler {{ tel }}
                          </a>
                        </p>
                      } @else {
                        <p class="mt-3 text-amber-900/90">Numéro de téléphone du site non disponible.</p>
                      }
                    } @else {
                      <p class="mt-3 text-amber-900/90">Numéro de téléphone du site non disponible.</p>
                    }
                  </div>
                }
              </section>
              @if (siteDetails()) {
                <section
                  class="flex flex-col gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 md:flex-row mt-6"
                >
                  <div class="min-w-0 flex-1 space-y-2 text-sm text-slate-700">
                    <div class="flex w-full items-start justify-between gap-2">
                      <div class="min-w-0 flex flex-col gap-2">
                        <h3 class="text-base font-semibold text-slate-900">{{ siteDetails()!.name }}</h3>
                        <p class="flex gap-2 text-slate-700">
                          <svg
                            class="mt-0.5 h-4 w-4 shrink-0 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <span class="min-w-0">
                            <span class="site_address">{{ siteDetails()!.address }}</span
                            ><br />
                            <span class="site_zip">{{ siteDetails()!.zip }} </span>
                            <span class="site_city">{{ siteDetails()!.city }}</span>
                          </span>
                        </p>
                        <p class="site_phone flex items-start gap-2">
                          <svg
                            class="mt-0.5 h-4 w-4 shrink-0 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                          <span>{{ siteDetails()!.phone }}</span>
                        </p>
                      </div>
                      @if (siteDetails()!.picture) {
                        <div class="shrink-0 md:w-40">
                          <img
                            [src]="siteDetails()!.picture"
                            alt=""
                            class="max-h-40 w-full rounded-md object-cover"
                          />
                        </div>
                      }
                    </div>
                    @if (siteOpeningHoursRows().length > 0) {
                      <div class="mt-3 border-t border-slate-200 pt-3">
                        <h4
                          class="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          <svg
                            class="h-5 w-5 shrink-0 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Horaires habituels
                        </h4>
                        <dl class="space-y-1.5 text-slate-700">
                          @for (row of siteOpeningHoursRows(); track row.label) {
                            <div class="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                              <dt class="min-w-[6.5rem] font-medium text-slate-600">{{ row.label }}</dt>
                              <dd class="min-w-0 text-slate-800">{{ row.hours }}</dd>
                            </div>
                          }
                        </dl>
                      </div>
                    }
                    @if (siteDetails()!.informations != null && siteDetails()!.informations != '') {
                      <div class="site_informations max-w-none border-t border-slate-200 pt-3 text-sm text-slate-700 [&_a]:text-red-700 [&_a]:underline">
                        <h4
                          class="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          <svg
                            class="h-5 w-5 shrink-0 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Informations complémentaires
                        </h4>
                        <div class="[&_p]:mb-2" [innerHTML]="siteDetails()!.informations"></div>
                      </div>
                    }
                    @if (siteDetails()!.info) {
                      <div class="site_info max-w-none border-t border-slate-200 pt-3 text-sm text-slate-700 [&_a]:text-red-700 [&_a]:underline">
                        <h4
                          class="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          <svg
                            class="h-5 w-5 shrink-0 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          Informations pratiques
                        </h4>
                        <div class="[&_p]:mb-2" [innerHTML]="siteDetails()!.info"></div>
                      </div>
                    }
                    @if (siteDetails()!.parking) {
                      <div class="site_parking max-w-none border-t border-slate-200 pt-3 text-sm text-slate-700 [&_a]:text-red-700 [&_a]:underline">
                        <h4
                          class="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          <span
                            class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-red-600 bg-white text-[0.65rem] font-bold leading-none text-red-600"
                            aria-hidden="true"
                            >P</span
                          >
                          Parking
                        </h4>
                        <div class="[&_p]:mb-2" [innerHTML]="siteDetails()!.parking"></div>
                      </div>
                    }
                    @if (siteDetails()!.transports_commun) {
                      <div class="site_transports_commun max-w-none border-t border-slate-200 pt-3 text-sm text-slate-700 [&_a]:text-red-700 [&_a]:underline">
                        <h4
                          class="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          <svg
                            class="h-5 w-5 shrink-0 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M8 6v6"
                            />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M15 6v6"
                            />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M2 12h19.6"
                            />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"
                            />
                            <circle cx="7" cy="18" r="2" fill="currentColor" stroke="none" />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 18h5"
                            />
                            <circle cx="16" cy="18" r="2" fill="currentColor" stroke="none" />
                          </svg>
                          Transports en commun
                        </h4>
                        <div class="[&_p]:mb-2" [innerHTML]="siteDetails()!.transports_commun"></div>
                      </div>
                    }
                    @if (siteDetails()!.accessibilite_pmr) {
                      <div class="site_accessibilite_pmr max-w-none border-t border-slate-200 pt-3 text-sm text-slate-700 [&_a]:text-red-700 [&_a]:underline">
                        <h4
                          class="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="h-5 w-5 shrink-0 text-red-600"
                            aria-hidden="true"
                          >
                            <circle cx="16" cy="4" r="2" fill="currentColor" stroke="none" />
                            <path d="m18 19 1-7-6 1" />
                            <path d="m5 8 3-3 5.5 3-2.36 3.5" />
                            <path d="M4.24 14.5a5 5 0 0 0 6.88 6" />
                            <path d="M13.76 17.5a5 5 0 0 0-6.88-6" />
                          </svg>
                          Accessibilité PMR
                        </h4>
                        <div class="[&_p]:mb-2" [innerHTML]="siteDetails()!.accessibilite_pmr"></div>
                      </div>
                    }
                  </div>
                </section>
              }
            }
          }
          @case (3) {
            @if (selectedSiteId() !== null) {
              <section class="space-y-4">
                <h2 class="sr-only">Choisir une date</h2>
                @if (loadingDates()) {
                  <p class="text-sm text-slate-600">Chargement des dates…</p>
                } @else if (datesError()) {
                  <div
                    class="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  >
                    <span>{{ datesError() }}</span>
                  </div>
                } @else {
                  <div class="space-y-2 mb-4 border-b border-slate-200 pb-4">
                    <p class="text-center text-sm text-slate-600 font-bold mb-4">
                      Prenez 2 minutes pour vérifier que vous êtes bien dans les conditions pour donner votre @if (selectedRdvType() === 'sang') { sang } @else if (selectedRdvType() === 'plasma') { plasma }.
                    </p>
                    <p class="text-center">
                      @if (selectedRdvType() === 'plasma') {
                        <button
                          type="button"
                          class="bg-plasma text-white px-4 py-2 rounded-md transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-plasma focus:ring-offset-2"
                          (click)="openTestEligibilityPlasmaModal()"
                        >
                          Faire le test
                        </button>
                      } @else {
                        <button
                          type="button"
                          class="bg-sang text-white px-4 py-2 rounded-md transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-sang focus:ring-offset-2"
                          (click)="openTestEligibilitySangModal()"
                        >
                          Faire le test
                        </button>
                      }
                    </p>
                  </div>
                  <p class="text-center text-sm text-slate-600 mt-4">
                    Les dates grisées ne sont pas réservables en ligne. Choisissez un jour mis en évidence.
                  </p>
                  <div class="mx-auto w-full max-w-lg">
                    <div class="flex items-stretch gap-2 sm:gap-3">
                      @if (datePickerCalendar().length > 1) {
                        <button
                          type="button"
                          class="flex shrink-0 items-center justify-center self-center rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-600/25 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-white sm:p-3"
                          [disabled]="!canCalendarMonthPrev()"
                          (click)="calendarMonthPrev()"
                          aria-label="Mois précédent"
                        >
                          <svg
                            class="h-6 w-6"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            aria-hidden="true"
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15 18l-6-6 6-6" />
                          </svg>
                        </button>
                      }
                      @if (displayedCalendarMonthBlock(); as block) {
                        <article
                          class="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 calendar-height"
                          [attr.aria-label]="'Calendrier ' + block.title"
                        >
                          <h3 class="mb-4 text-center text-lg font-semibold capitalize text-slate-900">
                            {{ block.title }}
                          </h3>
                          <div class="mb-2 grid grid-cols-7 gap-1">
                            @for (h of calendarWeekdayHeaders; track h) {
                              <div
                                class="py-1 text-center text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs"
                              >
                                {{ h }}
                              </div>
                            }
                          </div>
                          @for (week of block.weeks; track weekTrackKey(week)) {
                            <div class="mb-1 grid grid-cols-7 gap-1">
                              @for (cell of week; track cell.key) {
                                @if (cell.dayNum === null) {
                                  <div class="h-10 sm:h-9" aria-hidden="true"></div>
                                } @else if (!cell.selectable) {
                                  <div
                                    class="flex h-10 w-full items-center justify-center rounded-md border border-transparent bg-slate-100 text-sm text-slate-400 sm:h-9"
                                    aria-hidden="true"
                                  >
                                    {{ cell.dayNum }}
                                  </div>
                                } @else {
                                  <button
                                    type="button"
                                    class="flex h-10 w-full cursor-pointer items-center justify-center rounded-md border text-sm font-medium text-slate-900 transition hover:border-red-400 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-600/30 sm:h-9"
                                    [class.border-red-600]="isDateSelected(cell.iso!)"
                                    [class.bg-red-600]="isDateSelected(cell.iso!)"
                                    [class.text-white]="isDateSelected(cell.iso!)"
                                    [class.border-slate-200]="!isDateSelected(cell.iso!)"
                                    [class.bg-white]="!isDateSelected(cell.iso!)"
                                    [attr.aria-label]="'Choisir le ' + formatDateFr(cell.iso!)"
                                    [attr.aria-pressed]="isDateSelected(cell.iso!)"
                                    (click)="pickCalendarDate(cell)"
                                  >
                                    {{ cell.dayNum }}
                                  </button>
                                }
                              }
                            </div>
                          }
                        </article>
                      }
                      @if (datePickerCalendar().length > 1) {
                        <button
                          type="button"
                          class="flex shrink-0 items-center justify-center self-center rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-600/25 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-white sm:p-3"
                          [disabled]="!canCalendarMonthNext()"
                          (click)="calendarMonthNext()"
                          aria-label="Mois suivant"
                        >
                          <svg
                            class="h-6 w-6"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            aria-hidden="true"
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 18l6-6-6-6" />
                          </svg>
                        </button>
                      }
                    </div>
                  </div>
                }
              </section>
            }
          }
          @case (4) {
            @if (selectedDate() !== null) {
              <section class="space-y-3">
                <h2 class="sr-only">Choisir un horaire</h2>
                @if (slotsDateLabel()) {
                  <p class="text-sm text-slate-600">{{ slotsDateLabel() }}</p>
                }
                @if (loadingSlots()) {
                  <p class="text-sm text-slate-600">Chargement des créneaux…</p>
                } @else if (slotsError()) {
                  <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    {{ slotsError() }}
                  </div>
                } @else {
                  <p class="text-sm text-slate-600 text-center font-bold">
                    Choisissez un horaire de prélèvement
                  </p>

                  @if (withoutRdvHourBegin() && withoutRdvHourEnd()) {
                    <p
                      class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-center text-sm text-amber-950"
                    >
                      Les dons se font sans rendez-vous de {{ withoutRdvHourBegin() }} à {{ withoutRdvHourEnd() }}.
                    </p>
                  }

                  <div class="flex flex-wrap justify-center gap-2">
                    @for (slot of slots(); track slot.value) {
                      <button
                        type="button"
                        class="rounded-md border px-3 py-2 text-sm transition disabled:opacity-100"
                        [disabled]="slot.total === null"
                        [class.cursor-not-allowed]="slot.total === null"
                        [class.cursor-pointer]="slot.total !== null"
                        [class.border-slate-200]="slot.total === null"
                        [class.bg-slate-100]="slot.total === null"
                        [class.text-slate-400]="slot.total === null"
                        [class.border-red-600]="slot.total !== null && isTimeSelected(slot.value)"
                        [class.bg-red-600]="slot.total !== null && isTimeSelected(slot.value)"
                        [class.text-white]="slot.total !== null && isTimeSelected(slot.value)"
                        [class.border-slate-300]="slot.total !== null && !isTimeSelected(slot.value)"
                        [class.bg-white]="slot.total !== null && !isTimeSelected(slot.value)"
                        [class.text-slate-800]="slot.total !== null && !isTimeSelected(slot.value)"
                        [class.hover:border-red-400]="slot.total !== null && !isTimeSelected(slot.value)"
                        [class.hover:bg-red-50]="slot.total !== null && !isTimeSelected(slot.value)"
                        (click)="selectTime(slot.value)"
                      >
                        {{ slot.label }}
                      </button>
                    }
                  </div>
                }
              </section>
            }
          }
          @case (5) {
            @if (showDetailsBlock()) {
              <section class="space-y-4">
                <h2 class="sr-only">Tes coordonnées</h2>

                <form class="space-y-4" [formGroup]="detailsForm" (ngSubmit)="onSubmitDetails()" novalidate>
              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700" for="rdv-firstname"
                    >Prénom *</label
                  >
                  <input
                    id="rdv-firstname"
                    type="text"
                    formControlName="firstname"
                    autocomplete="given-name"
                    class="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/20"
                    [class.border-red-500]="
                      detailsForm.controls.firstname.invalid && detailsForm.controls.firstname.touched
                    "
                  />
                  @if (detailsForm.controls.firstname.invalid && detailsForm.controls.firstname.touched) {
                    <p class="mt-1 text-sm text-red-600">Requis.</p>
                  }
                </div>
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700" for="rdv-lastname"
                    >Nom *</label
                  >
                  <input
                    id="rdv-lastname"
                    type="text"
                    formControlName="lastname"
                    autocomplete="family-name"
                    class="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/20"
                    [class.border-red-500]="
                      detailsForm.controls.lastname.invalid && detailsForm.controls.lastname.touched
                    "
                  />
                  @if (detailsForm.controls.lastname.invalid && detailsForm.controls.lastname.touched) {
                    <p class="mt-1 text-sm text-red-600">Requis.</p>
                  }
                </div>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700" for="rdv-email"
                    >E-mail *</label
                  >
                  <input
                    id="rdv-email"
                    type="email"
                    formControlName="email"
                    autocomplete="email"
                    class="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/20"
                    [class.border-red-500]="
                      detailsForm.controls.email.invalid && detailsForm.controls.email.touched
                    "
                  />
                  @if (detailsForm.controls.email.hasError('required') && detailsForm.controls.email.touched) {
                    <p class="mt-1 text-sm text-red-600">Requis.</p>
                  } @else if (
                    detailsForm.controls.email.hasError('email') && detailsForm.controls.email.touched
                  ) {
                    <p class="mt-1 text-sm text-red-600">E-mail invalide.</p>
                  }
                </div>
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700" for="rdv-email-confirm"
                    >Confirmation e-mail *</label
                  >
                  <input
                    id="rdv-email-confirm"
                    type="email"
                    formControlName="emailConfirm"
                    autocomplete="email"
                    class="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/20"
                    [class.border-red-500]="
                      (detailsForm.controls.emailConfirm.invalid ||
                        detailsForm.hasError('emailsMismatch')) &&
                      detailsForm.controls.emailConfirm.touched
                    "
                  />
                  @if (
                    detailsForm.hasError('emailsMismatch') && detailsForm.controls.emailConfirm.touched
                  ) {
                    <p class="mt-1 text-sm text-red-600">Les deux adresses ne correspondent pas.</p>
                  } @else if (
                    detailsForm.controls.emailConfirm.invalid && detailsForm.controls.emailConfirm.touched
                  ) {
                    <p class="mt-1 text-sm text-red-600">Requis.</p>
                  }
                </div>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700" for="rdv-phone"
                    >Téléphone *</label
                  >
                  <input
                    id="rdv-phone"
                    type="tel"
                    formControlName="phone"
                    autocomplete="tel"
                    class="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/20"
                    [class.border-red-500]="
                      detailsForm.controls.phone.invalid && detailsForm.controls.phone.touched
                    "
                  />
                  @if (detailsForm.controls.phone.invalid && detailsForm.controls.phone.touched) {
                    <p class="mt-1 text-sm text-red-600">Requis.</p>
                  }
                </div>
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700" for="rdv-birth"
                    >Date de naissance *</label
                  >
                  <input
                    id="rdv-birth"
                    type="date"
                    formControlName="birthdate"
                    class="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/20"
                    [class.border-red-500]="
                      detailsForm.controls.birthdate.invalid && detailsForm.controls.birthdate.touched
                    "
                  />
                  @if (detailsForm.controls.birthdate.hasError('required') && detailsForm.controls.birthdate.touched) {
                    <p class="mt-1 text-sm text-red-600">Requis.</p>
                  } @else if (
                    detailsForm.controls.birthdate.hasError('tooYoung') &&
                    detailsForm.controls.birthdate.touched
                  ) {
                    <p class="mt-1 text-sm text-red-600">Vous devez avoir au moins 18 ans.</p>
                  }
                  <p class="mt-1 text-xs text-slate-500">Format : jour / mois / année (vous devez être majeur).</p>
                </div>
              </div>

              <div class="grid gap-6 sm:grid-cols-2">
                <fieldset>
                  <legend class="mb-2 text-sm font-medium text-slate-700">
                    Avez-vous déjà donné du sang ? *
                  </legend>
                  <div class="flex flex-wrap gap-4">
                    <label class="inline-flex items-center gap-2 text-sm">
                      <input type="radio" formControlName="hasAlreadyDonated" value="OUI" />
                      Oui
                    </label>
                    <label class="inline-flex items-center gap-2 text-sm">
                      <input type="radio" formControlName="hasAlreadyDonated" value="NON" />
                      Non
                    </label>
                  </div>
                  @if (
                    detailsForm.controls.hasAlreadyDonated.invalid &&
                    detailsForm.controls.hasAlreadyDonated.touched
                  ) {
                    <p class="mt-1 text-sm text-red-600">Choisis une option.</p>
                  }
                </fieldset>
                <fieldset>
                  <legend class="mb-2 text-sm font-medium text-slate-700">
                    Avez-vous déjà donné du plasma ? *
                  </legend>
                  <div class="flex flex-wrap gap-4">
                    <label class="inline-flex items-center gap-2 text-sm">
                      <input type="radio" formControlName="hasAlreadyDonatedPlasma" value="OUI" />
                      Oui
                    </label>
                    <label class="inline-flex items-center gap-2 text-sm">
                      <input type="radio" formControlName="hasAlreadyDonatedPlasma" value="NON" />
                      Non
                    </label>
                  </div>
                  @if (
                    detailsForm.controls.hasAlreadyDonatedPlasma.invalid &&
                    detailsForm.controls.hasAlreadyDonatedPlasma.touched
                  ) {
                    <p class="mt-1 text-sm text-red-600">Choisis une option.</p>
                  }
                </fieldset>
              </div>

              <!--<div class="rounded-md border border-slate-200 bg-slate-50 p-4">
                <p class="text-sm font-semibold text-slate-900">
                  Action de parrainage – ÉTÉ 2026
                </p>
                <p class="mt-2 text-sm text-slate-700">
                  Venez donner à deux et doublez vos points fidélités.
                </p>
                <fieldset class="mt-4">
                  <legend class="mb-3 text-sm font-medium text-slate-700">
                    Je souhaite participer à l'action
                  </legend>
                  <div class="flex flex-col gap-3">
                    <label class="inline-flex items-start gap-2 text-sm text-slate-800">
                      <input
                        type="radio"
                        formControlName="parrainage"
                        value="non"
                        class="mt-0.5 size-4 shrink-0 border-slate-300 text-red-600 focus:ring-red-600"
                      />
                      <span>Non</span>
                    </label>
                    <label class="inline-flex items-start gap-2 text-sm text-slate-800">
                      <input
                        type="radio"
                        formControlName="parrainage"
                        value="parrain"
                        class="mt-0.5 size-4 shrink-0 border-slate-300 text-red-600 focus:ring-red-600"
                      />
                      <span>Oui, j'accompagnerai un nouveau donneur</span>
                    </label>
                    <label class="inline-flex items-start gap-2 text-sm text-slate-800">
                      <input
                        type="radio"
                        formControlName="parrainage"
                        value="filleul"
                        class="mt-0.5 size-4 shrink-0 border-slate-300 text-red-600 focus:ring-red-600"
                      />
                      <span>Oui, je suis un nouveau donneur et je serai accompagné de mon parrain</span>
                    </label>
                  </div>
                </fieldset>
                <p class="mt-4 text-xs text-slate-600">
                  Action organisée du 1/7/2026 au 30/09/2026.
                  <em>
                    <a
                      href="https://merci.donneurdesang.be/parrainage/"
                      class="font-medium text-red-700 underline hover:text-red-800"
                      target="_blank"
                      rel="noopener noreferrer"
                      >En savoir plus sur l'action
                    </a>
                  </em>
                </p>
              </div>-->

              <div class="rounded-md border border-slate-200 bg-slate-50 p-4">
                <label class="flex cursor-pointer gap-3 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    formControlName="acceptDonnees"
                    class="mt-1 size-4 shrink-0 rounded border-slate-300 text-red-600 focus:ring-red-600"
                  />
                  <span>
                    J’accepte le traitement de mes données personnelles conformément à la
                    <a
                      href="https://www.croix-rouge.be/donnees-personnelles/"
                      class="font-medium text-red-700 underline hover:text-red-800"
                      target="_blank"
                      rel="noopener noreferrer"
                      >politique de confidentialité</a
                    >.
                  </span>
                </label>
                @if (
                  detailsForm.controls.acceptDonnees.invalid && detailsForm.controls.acceptDonnees.touched
                ) {
                  <p class="mt-2 text-sm text-red-600">Vous devez accepter pour continuer.</p>
                }
              </div>

              @if (apiError()) {
                <div
                  class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                  role="alert"
                >
                  {{ apiError() }}
                </div>
              }

              <div class="flex justify-end pt-2">
                <button
                  type="submit"
                  class="inline-flex min-w-[10rem] items-center justify-center rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  [disabled]="submitting()"
                >
                  @if (submitting()) {
                    <span>Envoi…</span>
                  } @else {
                    <span>Confirmer le rendez-vous</span>
                  }
                </button>
              </div>
                </form>
              </section>
            }
          }
        }
      </div>
    }
  }
</div>

@if (testEligibilitySangModalOpen()) {
  <div
    class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
    role="presentation"
    (click)="closeTestEligibilitySangModal()"
  >
    <div
      class="flex max-h-[90vh] w-full max-w-[800px] flex-col overflow-hidden rounded-lg bg-white shadow-xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="eligibility-test-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 id="eligibility-test-title" class="text-base font-semibold text-slate-900">
          Test d’éligibilité au don de sang
        </h2>
        <button
          type="button"
          class="rounded-md p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-600/30"
          (click)="closeTestEligibilitySangModal()"
          aria-label="Fermer la fenêtre"
        >
          <svg
            class="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <iframe
        [src]="testEligibilitySangIframeSrc" style="min-height: 570px;height: 570px;"
        class="min-h-0 w-full flex-1 border-0"
        title="Test d’éligibilité au don"
      ></iframe>
    </div>
  </div>
}

@if (testEligibilityPlasmaModalOpen()) {
  <div
    class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
    role="presentation"
    (click)="closeTestEligibilityPlasmaModal()"
  >
    <div
      class="flex max-h-[90vh] w-full max-w-[800px] flex-col overflow-hidden rounded-lg bg-white shadow-xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="eligibility-test-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 id="eligibility-test-title" class="text-base font-semibold text-slate-900">
          Test d’éligibilité au don de plasma
        </h2>
        <button
          type="button"
          class="rounded-md p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-600/30"
          (click)="closeTestEligibilityPlasmaModal()"
          aria-label="Fermer la fenêtre"
        >
          <svg
            class="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <iframe
        [src]="testEligibilityPlasmaIframeSrc"
        class="min-h-0 w-full flex-1 border-0" style="min-height: 570px;height: 570px;"
        title="Test d’éligibilité au don"
      ></iframe>
    </div>
  </div>
}
